import {
	createAsyncThunk,
	createSlice,
	nanoid,
	type PayloadAction,
} from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import i18next from 'i18next'
import { cloneDeep, isEqual, orderBy } from 'lodash'
import type {
	CreateInvoicesInput,
	DraftEmailDataTypeInput,
	DraftsInput,
	EmailData,
	UpdateInvoicesInput,
} from 'src/API'
import config from 'src/aws-exports'
import * as customMutations from 'src/graphql/customMutations'
import * as mutations from 'src/graphql/mutations'
import * as queries from 'src/graphql/queries'
import { billitApi } from 'src/redux/api/graphql/billitApi'
import { fetchContacts } from 'src/redux/slices/contacts'
import {
	fetchProposals,
	setCurrentDraftInvoiceId,
	setCurrentProposal,
	setCurrentProposalInvoices,
} from 'src/redux/slices/proposals'
import type { RootState, ThunkAppDispatch } from 'src/redux/store'
import type {
	ReduxCompany,
	ReduxContact,
	ReduxInvoices,
	ReduxProposals,
} from 'src/types/apiType'
import type { ApiStatus } from 'src/types/miscType'
import type { QuoteTotals } from 'src/types/quoteTotalsTypes'
import { API, Auth, graphqlOperation, Storage } from 'src/utils/amplify/legacy'
import { getBankAccountProfile } from 'src/utils/bankAccountProfiles'
import { getErrorMessage } from 'src/utils/error'
import { fetchInvoicesByTenantIdUtil } from 'src/utils/fetchUtils'
import {
	getDueBasedInvoiceStatus,
	recomputeOriginalInvoiceStatus,
} from 'src/utils/invoiceUtils/paymentStatus'
import {
	calculateCreditInvoiceTotals,
	calculateProposalAndInvoiceTotals,
	createUpdateData,
} from 'src/utils/invoiceUtils/proposalUtils'
import { makeErrorToast, makeSuccessToast, msg } from 'src/utils/makeToast'
import { updateCompanyInfoWithDeltaSyncSuppress } from 'src/utils/miscellaneous'
import { captureExceptionAndThrow } from 'src/utils/posthogException'

type AsyncThunkConfig = {
	state: RootState
	dispatch: ThunkAppDispatch
	rejectValue: unknown
}

type GraphqlResponse<T> = {
	data: T
}

type DateRangeArgs = {
	startDate?: string | null
	endDate?: string | null
}

type NumericDraftKey =
	| 'proposalAmountPercentage'
	| 'amount'
	| 'amountAfterTax'
	| 'taxAmount'
	| 'balancePercentage'
	| 'balanceAmount'

type InvoiceDraftFormValue = Omit<DraftsInput, NumericDraftKey> & {
	proposalAmountPercentage?: string | number | null
	amount?: string | number | null
	amountAfterTax?: string | number | null
	taxAmount?: string | number | null
	balancePercentage?: string | number | null
	balanceAmount?: string | number | null
	readyStatus?: boolean | null
}

type InvoiceFormData = {
	contact: string
	contactId?: string | null
	drafts: InvoiceDraftFormValue[]
	invoiceCount: string
	isDetailed?: boolean | null
	showBidLetter?: boolean | null
	isClientTaxRate?: boolean | null
	companyLogo?: string | null
	bankAccount?: string | null
	contactEmail?: string | null
	contactCurrency?: string | null
	contactTaxRate?: string | null
}

type CreditInvoiceData = {
	amount: string | number
	invoiceAmountPercentage: string | number
	invoiceDate?: string | Date | null
	comments?: string | null
	invoiceTotal?: number | null
}

type CreditInvoiceArgs = DateRangeArgs & {
	creditInvoiceData: CreditInvoiceData
	originalInvoiceData: ReduxInvoices
}

type CreateInvoiceArgs = {
	formData: InvoiceFormData
	currentProposal: ReduxProposals
	index: number
	quoteTotals?: QuoteTotals | null
	proposalData?: Partial<InvoiceDraftFormValue>
}

type UpdateInvoiceArgs = {
	proposalData: Partial<InvoiceDraftFormValue>
	formData: InvoiceFormData
	currentProposal: ReduxProposals
	index: number
}

type PaymentStatusArgs = DateRangeArgs & {
	date: string | Date
	currentInvoice: string
}

type InvoiceIdDateRangeArgs = DateRangeArgs & {
	currentInvoice: string
}

type InvoiceDailyUpdateArgs = DateRangeArgs & {
	invoiceData: Pick<ReduxInvoices, 'id'>[]
}

type ViewPdfRow = ReduxInvoices & {
	contact?: unknown
	proposals?: ReduxProposals | null
}

type ViewPdfArgs = {
	row: ViewPdfRow
	actionType?: string
}

type UpdateInvoiceLastExportedDateArgs = DateRangeArgs & {
	invoiceIds: string[]
}

type SendClientInvoiceArgs = DateRangeArgs & {
	invoiceId: string
	emailData: EmailData
	pdfBlob: Blob | File | ArrayBuffer | string | null
	currentLanguage: string
}

type SaveEmailAsDraftArgs = DateRangeArgs & {
	invoiceId: string
	draftEmailData: DraftEmailDataTypeInput
}

type ImportedInvoice = Partial<CreateInvoicesInput> & {
	clientId: string
	contactId?: string | null
	invoiceDate?: string | number | Date | null
	invoiceDueDate?: string | number | Date | null
	bankProfileNumber?: string | null
	isDetailed?: boolean | null
	showBidLetter?: boolean | null
	isClientTaxRate?: boolean | null
}

type InvoiceWithContact = ReduxInvoices & {
	contact?: unknown
}

type ResolverStatusResponse = {
	status?: string | null
	isSuccessful?: boolean | null
}

type S3SaveResult = {
	fileUploadDetails: {
		bucket: string
		key: string
		region: string
		identityId?: string
	}
	fileName: string
}

type DialogStatusPayload = {
	status: boolean
}

type InvoicesState = {
	invoicesData: ReduxInvoices[]
	unpaidInvoicesData: ReduxInvoices[]
	viewPdfStatus: ApiStatus
	fetchInvoicesApiStatus: ApiStatus
	fetchUnpaidInvoicesApiStatus: ApiStatus
	createInvoiceApiStatus: ApiStatus
	previewCreditInvoiceApiStatus: ApiStatus
	importInvoicesApiStatus: ApiStatus
	updateInvoiceApiStatus: ApiStatus
	updatePaymentApiStatus: ApiStatus
	updateArchiveApiStatus: ApiStatus
	updateInvoiceLastExportedDate: ApiStatus
	updateInvoiceLastExportedDateApiStatus: ApiStatus
	sendClientInvoiceApiStatus: ApiStatus
	saveEmailAsDraftApiStatus: ApiStatus
	viewPdfModalOpenStatus: boolean
	viewImportedPdfModalOpenStatus: boolean
	creditInvoiceModalOpenStatus: boolean
	importInvoicesModalOpen: boolean
	invoicePaidConfirmationDialogOpenStatus: boolean
	invoiceUnpaidConfirmationDialogOpenStatus: boolean
	invoiceArchivedConfirmationDialogOpenStatus: boolean
	invoiceDownloadConfirmationDialogOpenStatus: boolean
	invoiceDownloadConfirmationFromPagesDialogOpenStatus: boolean
	preivewCreditInvoiceModalOpenStatus: boolean
	confirmInvoiceExportDialogOpenStatus: boolean
	sendClientInvoiceEmailModalOpenStatus: boolean
	syncInvoiceContactModalOpenStatus: boolean
	invoiceToSync: ReduxInvoices | null
	originalInvoiceData: ReduxInvoices | null
	currentInvoice: ReduxInvoices | null
	previewCreditInvoiceData: Partial<ReduxInvoices> | null
	error: string | null
}

const graphqlRequest = async <T>(
	operation: ReturnType<typeof graphqlOperation>
): Promise<GraphqlResponse<T>> => {
	return (await API.graphql(operation)) as GraphqlResponse<T>
}

const zeroPad = (num: number, places: number) =>
	String(num).padStart(places, '0')

const parseNumber = (value: string | number | null | undefined) =>
	Number.parseFloat(String(value ?? 0))

const getNextInvoiceCounter = (
	companyDetails: Pick<ReduxCompany, 'invoiceCount' | 'invoicingYear'>
) => {
	const currentInvoiceCount = Number(companyDetails.invoiceCount ?? 0)
	let invoiceCount =
		(Number.isFinite(currentInvoiceCount)
			? Math.trunc(currentInvoiceCount)
			: 0) + 1
	let invoicingYear = companyDetails.invoicingYear ?? dayjs().toISOString()

	if (
		companyDetails.invoiceCount !== null &&
		companyDetails.invoiceCount !== undefined
	) {
		invoicingYear = dayjs().toISOString()
	}
	if (companyDetails.invoicingYear === null) {
		invoicingYear = dayjs().toISOString()
	} else {
		const today = dayjs().year()
		const oldInvoicingYear = dayjs(companyDetails.invoicingYear).year()
		if (today > oldInvoicingYear) {
			invoicingYear = dayjs().toISOString()
			invoiceCount = 1
		}
	}

	return {
		invoiceCount,
		invoiceNumber: zeroPad(invoiceCount, 4),
		invoicingYear,
	}
}

const normalizeCompanyInvoiceCounterUpdate = (update: {
	id: string
	invoiceCount?: string | number | null
	invoicingYear?: string | null
}): Partial<ReduxCompany> => ({
	id: update.id,
	invoiceCount:
		typeof update.invoiceCount === 'string'
			? Number.parseInt(update.invoiceCount, 10)
			: update.invoiceCount,
	invoicingYear: update.invoicingYear,
})

const getUnknownErrorMessage = (error: unknown) => {
	if (typeof error === 'object' && error !== null && 'message' in error) {
		const message = (error as { message?: unknown }).message
		return typeof message === 'string' ? message : null
	}
	return null
}

const initialState: InvoicesState = {
	invoicesData: [],
	unpaidInvoicesData: [],
	viewPdfStatus: 'idle',
	fetchInvoicesApiStatus: 'idle',
	fetchUnpaidInvoicesApiStatus: 'idle',
	createInvoiceApiStatus: 'idle',
	previewCreditInvoiceApiStatus: 'idle',
	importInvoicesApiStatus: 'idle',
	updateInvoiceApiStatus: 'idle',
	updatePaymentApiStatus: 'idle',
	updateArchiveApiStatus: 'idle',
	updateInvoiceLastExportedDate: 'idle',
	updateInvoiceLastExportedDateApiStatus: 'idle',
	sendClientInvoiceApiStatus: 'idle',
	saveEmailAsDraftApiStatus: 'idle',
	viewPdfModalOpenStatus: false,
	viewImportedPdfModalOpenStatus: false,
	creditInvoiceModalOpenStatus: false,
	importInvoicesModalOpen: false,
	invoicePaidConfirmationDialogOpenStatus: false,
	invoiceUnpaidConfirmationDialogOpenStatus: false,
	invoiceArchivedConfirmationDialogOpenStatus: false,
	invoiceDownloadConfirmationDialogOpenStatus: false,
	invoiceDownloadConfirmationFromPagesDialogOpenStatus: false,
	preivewCreditInvoiceModalOpenStatus: false,
	confirmInvoiceExportDialogOpenStatus: false,
	sendClientInvoiceEmailModalOpenStatus: false,
	syncInvoiceContactModalOpenStatus: false,
	invoiceToSync: null,
	originalInvoiceData: null,
	currentInvoice: null,
	previewCreditInvoiceData: null,
	error: null,
}

export const fetchInvoices = createAsyncThunk<
	ReduxInvoices[],
	DateRangeArgs,
	AsyncThunkConfig
>(
	'invoices/fetchInvoices',
	async ({ startDate, endDate }, { getState, rejectWithValue }) => {
		console.log('startDate: ', startDate)
		const { auth } = getState()
		try {
			const invoicesData = await fetchInvoicesByTenantIdUtil(
				auth.userTableInfo?.currentCompanyTenantId,
				startDate,
				endDate
			)

			return invoicesData
		} catch (err) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
export const fetchUnpaidInvoices = createAsyncThunk<
	ReduxInvoices[],
	void,
	AsyncThunkConfig
>('invoices/fetchUnpaidInvoices', async (_, { getState, rejectWithValue }) => {
	const { auth } = getState()
	try {
		const invoicesData = await fetchInvoicesByTenantIdUtil(
			auth.userTableInfo?.currentCompanyTenantId
		)
		const unpaidInvoices = invoicesData?.filter(
			(inv) =>
				inv.paymentStatus !== 'Paid' &&
				inv.paymentStatus !== 'Nullified'
		)
		return unpaidInvoices
	} catch (err) {
		makeErrorToast(msg)
		return rejectWithValue(err)
	}
})
export const createCreditInvoice = createAsyncThunk<
	void,
	CreditInvoiceArgs,
	AsyncThunkConfig
>(
	'invoices/createCreditInvoice',
	async (
		{ creditInvoiceData, originalInvoiceData, startDate, endDate },
		{ getState, dispatch, rejectWithValue }
	) => {
		const { auth } = getState()
		try {
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User Table Info is not set')
			}
			const tenantId = userTableInfo.currentCompanyTenantId
			if (!tenantId) {
				return rejectWithValue('Tenant ID is not set')
			}
			delete creditInvoiceData.invoiceTotal

			const copyInvoiceData: Partial<ReduxInvoices> =
				cloneDeep(originalInvoiceData)
			// delete originalInvoiceData.id
			delete copyInvoiceData.tenantId
			// delete copyInvoiceData.proposalId
			delete copyInvoiceData.activeFlag
			delete copyInvoiceData.archivedDate
			delete copyInvoiceData.title
			delete copyInvoiceData.invoiceNumber
			delete copyInvoiceData.invoiceAmountPercentage
			delete copyInvoiceData.amount
			delete copyInvoiceData.amountAfterTax
			delete copyInvoiceData.taxAmount
			delete copyInvoiceData.balancePercentage
			delete copyInvoiceData.balanceAmount
			delete copyInvoiceData.invoiceDate
			delete copyInvoiceData.description
			delete copyInvoiceData.proposalsDraft
			delete copyInvoiceData.proposals
			delete copyInvoiceData.createdOn
			delete copyInvoiceData.updatedOn
			delete copyInvoiceData._version
			delete copyInvoiceData._deleted
			delete copyInvoiceData._lastChangedAt
			// Remove Billit fields - credit invoices should have their own Billit status
			delete copyInvoiceData.billitInboxItemId
			delete copyInvoiceData.billitPeppolStatus
			delete copyInvoiceData.billitPeppolError

			const {
				data: { getCompany: companyDetails },
			} = await graphqlRequest<{ getCompany: ReduxCompany }>(
				graphqlOperation(queries.getCompany, {
					id: tenantId,
				})
			)
			const {
				data: { getContact },
			} = await graphqlRequest<{ getContact: ReduxContact }>(
				graphqlOperation(queries.getContact, {
					id: originalInvoiceData.clientId,
				})
			)

			const argObject = {
				originalInvoiceData,
				creditInvoiceData,
				copyInvoiceData,
				companyDetails,
				getContact,
				tenantId,
			}
			const { newInvoice, updateOriginalInvoice, updateCompanyDetails } =
				calculateCreditInvoiceTotals(argObject)

			await API.graphql(
				graphqlOperation(customMutations.createInvoicesSimplified, {
					input: newInvoice,
				})
			)
			await API.graphql(
				graphqlOperation(customMutations.updateInvoicesSimplified, {
					input: updateOriginalInvoice,
				})
			)
			await updateCompanyInfoWithDeltaSyncSuppress(
				normalizeCompanyInvoiceCounterUpdate(updateCompanyDetails)
			)

			const logInput = {
				id: nanoid(),
				activeFlag: true,
				tenantId: userTableInfo.currentCompanyTenantId,
				name: userTableInfo.email,
				exportDate: dayjs().toISOString(),
				exportType: `Generated Invoice ${
					newInvoice?.invoiceNumber ?? ''
				}`,
				loggedLineId: newInvoice?.id,
			}
			const tInvoices = i18next.getFixedT(null, 'invoices')
			const {
				data: { createExportInfo },
			} = await graphqlRequest<{ createExportInfo: unknown }>(
				graphqlOperation(mutations.createExportInfo, {
					input: logInput,
				})
			)

			// Send credit invoice to Billit if credentials are configured
			if (companyDetails?.billitPartyId && companyDetails?.billitApiKey) {
				try {
					await dispatch(
						billitApi.endpoints.sendInvoiceToBillit.initiate({
							tenantId,
							invoiceId: newInvoice.id,
						})
					).unwrap()

					makeSuccessToast(tInvoices('billit.sentSuccess'))
				} catch (billitError) {
					// Log error but don't fail the credit invoice creation
					console.error(
						'Failed to send credit invoice to Billit:',
						billitError
					)
					// Show the actual error message from the API
					const errorMessage =
						typeof billitError === 'string'
							? billitError
							: getUnknownErrorMessage(billitError) ||
								tInvoices('billit.sentError')
					makeErrorToast(errorMessage)
				}
			}

			await dispatch(fetchInvoices({ startDate, endDate }))
			makeSuccessToast(tInvoices('toasts.createdInvoice'))
		} catch (err) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
export const previewCreditInvoice = createAsyncThunk<
	Partial<ReduxInvoices>,
	Omit<CreditInvoiceArgs, keyof DateRangeArgs>,
	AsyncThunkConfig
>(
	'invoices/previewCreditInvoice',
	async (
		{ creditInvoiceData, originalInvoiceData },
		{ getState, dispatch, rejectWithValue }
	) => {
		const { auth } = getState()
		try {
			const tenantId = auth.userTableInfo?.currentCompanyTenantId
			if (!tenantId) {
				return rejectWithValue('Tenant ID is not set')
			}
			delete creditInvoiceData.invoiceTotal

			const copyInvoiceData: Partial<ReduxInvoices> =
				cloneDeep(originalInvoiceData)
			// delete originalInvoiceData.id
			delete copyInvoiceData.tenantId
			// delete copyInvoiceData.proposalId
			delete copyInvoiceData.activeFlag
			delete copyInvoiceData.archivedDate
			delete copyInvoiceData.title
			delete copyInvoiceData.invoiceNumber
			delete copyInvoiceData.invoiceAmountPercentage
			delete copyInvoiceData.amount
			delete copyInvoiceData.amountAfterTax
			delete copyInvoiceData.taxAmount
			delete copyInvoiceData.balancePercentage
			delete copyInvoiceData.balanceAmount
			delete copyInvoiceData.invoiceDate
			delete copyInvoiceData.description
			delete copyInvoiceData.proposalsDraft
			delete copyInvoiceData.proposals
			delete copyInvoiceData.createdOn
			delete copyInvoiceData.updatedOn
			delete copyInvoiceData._version
			delete copyInvoiceData._deleted
			delete copyInvoiceData._lastChangedAt
			// Remove Billit fields - credit invoices should have their own Billit status
			delete copyInvoiceData.billitInboxItemId
			delete copyInvoiceData.billitPeppolStatus
			delete copyInvoiceData.billitPeppolError

			const {
				data: { getCompany: companyDetails },
			} = await graphqlRequest<{ getCompany: ReduxCompany }>(
				graphqlOperation(queries.getCompany, {
					id: tenantId,
				})
			)

			const {
				data: { getContact },
			} = await graphqlRequest<{ getContact: ReduxContact }>(
				graphqlOperation(queries.getContact, {
					id: originalInvoiceData.clientId,
				})
			)
			const argObject = {
				originalInvoiceData,
				creditInvoiceData,
				copyInvoiceData,
				companyDetails,
				getContact,
				tenantId,
			}
			const { newInvoice, updateOriginalInvoice, updateCompanyDetails } =
				calculateCreditInvoiceTotals(argObject)
			return newInvoice
		} catch (err) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const createInvoice = createAsyncThunk<
	void,
	CreateInvoiceArgs,
	AsyncThunkConfig
>(
	'invoices/createInvoice',
	async (
		{ formData, currentProposal, index, quoteTotals },
		{ getState, dispatch, rejectWithValue }
	) => {
		const tInvoices = i18next.getFixedT(null, 'invoices')
		try {
			const { auth } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User Table Info is not set')
			}
			const tenantId = userTableInfo.currentCompanyTenantId
			if (!tenantId) {
				return rejectWithValue('Tenant ID is not set')
			}
			const {
				data: { getCompany },
			} = await graphqlRequest<{ getCompany: ReduxCompany }>(
				graphqlOperation(queries.getCompany, {
					id: tenantId,
				})
			)

			const {
				data: { getContact },
			} = await graphqlRequest<{ getContact: ReduxContact }>(
				graphqlOperation(queries.getContact, { id: formData.contact })
			)
			const {
				data: { getProposals: updateDrafts },
			} = await graphqlRequest<{ getProposals: ReduxProposals }>(
				graphqlOperation(queries.getProposals, {
					id: currentProposal.id,
				})
			)

			delete updateDrafts._deleted
			delete updateDrafts._lastChangedAt
			delete updateDrafts.quote
			delete updateDrafts.invoices
			updateDrafts.clientId = formData.contact
			updateDrafts.clientName = getContact.name

			const proposalData = calculateProposalAndInvoiceTotals({
				getProposals: updateDrafts,
				newProposal: formData,
				getContact,
				quoteTotals,
				getCompany,
			})
			console.log('proposalData: ', proposalData)

			updateDrafts.drafts = proposalData.drafts
			console.log(
				'proposalData.invoiceCount: ',
				proposalData.invoiceCount
			)
			updateDrafts.noofInvoices = proposalData.invoiceCount
			updateDrafts.paymentTerms = proposalData.paymentTerms
			const { getProposals: updateProposalData, updateContact } =
				createUpdateData({
					getProposals: updateDrafts,
					newProposal: formData,
					getContact,
					getCompany,
				})

			if (
				updateDrafts.noofInvoices &&
				isNaN(Number.parseInt(updateDrafts.noofInvoices))
			) {
				makeErrorToast(
					'Something Unexpected Happened With Invoice Count!'
				)
			}

			const {
				data: { updateProposals: getUpdatedProposal },
			} = await graphqlRequest<{ updateProposals: ReduxProposals }>(
				graphqlOperation(customMutations.updateProposalsSimplified, {
					input: updateProposalData,
				})
			)
			const newInvoiceId = nanoid()
			const updatedDrafts = getUpdatedProposal.drafts ?? []
			const updatedDraft = updatedDrafts[index]
			if (!updatedDraft) {
				return rejectWithValue('Invoice draft is not set')
			}
			if (updatedDraft.generated === true) {
				makeErrorToast(tInvoices('errorToasts.invExists'))
			} else {
				const {
					invoiceCount,
					invoiceNumber: newInvoiceNumber,
					invoicingYear: newInvoicingyear,
				} = getNextInvoiceCounter(getCompany)

				const newCompanyDetails = {
					id: getCompany.id,
					invoiceCount,
					invoicingYear: newInvoicingyear,
				}

				const readyStatusArray = formData.drafts.map(
					(d) => d.readyStatus
				)
				if (readyStatusArray.every((r) => r === true)) {
					getUpdatedProposal.status = 'Generated'
				} else if (readyStatusArray.some((r) => r === true)) {
					getUpdatedProposal.status = 'Partially Generated'
				}
				delete getUpdatedProposal._deleted
				delete getUpdatedProposal._lastChangedAt
				delete getUpdatedProposal.quote
				delete getUpdatedProposal.invoices
				updatedDraft.generated = true
				updatedDraft.readyStatus = true
				updatedDraft.invoiceId = newInvoiceId
				getUpdatedProposal.clientId = formData.contact
				getUpdatedProposal.clientName = getContact.name

				const year = dayjs(newInvoicingyear).year()
				const newInvoice: CreateInvoicesInput = {
					id: newInvoiceId,
					tenantId,
					proposalId: currentProposal.id,
					activeFlag: true,
					clientId: formData.contact,
					contactId: formData.contactId,
					clientName: getContact.name,
					contactName: getUpdatedProposal.contactName,
					clientEmail: getUpdatedProposal.clientEmail,
					clientAddress: getUpdatedProposal.clientAddress,
					clientCountry: getUpdatedProposal.clientCountry,
					clientIdNumber: getUpdatedProposal.clientIdNumber,
					clientVatNumber: getUpdatedProposal.clientVatNumber,
					clientCurrency: getUpdatedProposal.clientCurrency,
					clientTaxRate: getUpdatedProposal.clientTaxRate,
					clientTaxType: getUpdatedProposal.clientTaxType,
					clientNote: getUpdatedProposal.clientNote,
					isDetailed: getUpdatedProposal.isDetailed,
					showBidLetter: getUpdatedProposal.showBidLetter,
					isClientTaxRate: getUpdatedProposal.isClientTaxRate,
					companyLogo: getUpdatedProposal.companyLogo,
					// selectedTaxRate: getUpdatedProposal.selectedTaxRate,
					// isCLientCurrency: getUpdatedProposal.isCLientCurrency,
					// selectedCurrency: getUpdatedProposal.selectedCurrency,
					bankProfileNumber: getUpdatedProposal.bankProfileNumber,
					invoicesBankProfileName:
						getUpdatedProposal.proposalsBankProfileName,
					invoicesBank: getUpdatedProposal.proposalsBank,
					invoicesIban: getUpdatedProposal.proposalsIban,
					invoicesBic: getUpdatedProposal.proposalsBic,
					invoicesAccountName:
						getUpdatedProposal.proposalsAccountName,
					title: `Invoice ${newInvoiceNumber}`,
					invoiceNumber: `${
						getCompany?.invoiceNumberPrefix ?? 'INV'
					}-${year}-${newInvoiceNumber}`,
					poNumber: updatedDraft.poNumber,
					quoteName: getUpdatedProposal.quoteName,
					quoteType: getUpdatedProposal.quoteType,
					quoteNumber: getUpdatedProposal.quoteNumber,
					projectName: getUpdatedProposal.projectName,
					projectNumber: getUpdatedProposal.projectNumber,
					invoiceAmountPercentage:
						updatedDraft.proposalAmountPercentage,
					amount: parseNumber(updatedDraft.amount),
					amountAfterTax: parseNumber(updatedDraft.amountAfterTax),
					taxAmount: parseNumber(updatedDraft.taxAmount),
					taxDetails: updatedDraft.taxDetails,
					sectionDetails: updatedDraft.sectionDetails,
					sectionDetailsByQuote: updatedDraft.sectionDetailsByQuote,
					sectionDetailsByAccSection:
						updatedDraft.sectionDetailsByAccSection,
					salesAccNumberType: getCompany?.salesAccNumberType,
					balancePercentage: parseNumber(
						updatedDraft.balancePercentage
					),
					balanceAmount: parseNumber(updatedDraft.balanceAmount),
					invoiceDate: updatedDraft.invoiceDate,
					invoiceDueDate: updatedDraft.invoiceDueDate,
					description: updatedDraft.description,
					paymentStatus: 'Pending',
					proposalsDraft: updatedDrafts as DraftsInput[],
					invoiceType: 'original',
					creditInvoiceGenerated: false,
				}

				if (getUpdatedProposal?.assignedTenantId) {
					newInvoice.assignedTenantId =
						getUpdatedProposal?.assignedTenantId
				}
				if (getUpdatedProposal?.assignedTenantName) {
					newInvoice.assignedTenantName =
						getUpdatedProposal?.assignedTenantName
				}

				await API.graphql(
					graphqlOperation(customMutations.createInvoicesSimplified, {
						input: newInvoice,
					})
				)
				const {
					data: { updateProposals: newCurrentProposal },
				} = await graphqlRequest<{
					updateProposals: ReduxProposals
				}>(
					graphqlOperation(
						customMutations.updateProposalsSimplified,
						{
							input: getUpdatedProposal,
						}
					)
				)
				await updateCompanyInfoWithDeltaSyncSuppress(newCompanyDetails)
				dispatch(setCurrentProposal(newCurrentProposal))
				if (Object.keys(updateContact).length > 0) {
					updateContact.id = getContact.id
					await API.graphql(
						graphqlOperation(mutations.updateContact, {
							input: updateContact,
						})
					)
				}
				const logInput = {
					id: nanoid(),
					activeFlag: true,
					tenantId: userTableInfo.currentCompanyTenantId,
					name: userTableInfo?.email,
					exportDate: dayjs().toISOString(),
					exportType: `Generated Invoice ${
						newInvoice?.invoiceNumber ?? ''
					}`,
					loggedLineId: newInvoice?.id,
				}
				const {
					data: { createExportInfo },
				} = await graphqlRequest<{ createExportInfo: unknown }>(
					graphqlOperation(mutations.createExportInfo, {
						input: logInput,
					})
				)
				await dispatch(fetchInvoices({}))
				await dispatch(fetchProposals())
				await dispatch(fetchContacts())
				makeSuccessToast(tInvoices('toasts.createdInvoice'))
			}
			const {
				data: {
					invoicesByProposalId: { items },
				},
			} = await graphqlRequest<{
				invoicesByProposalId: { items: ReduxInvoices[] }
			}>(
				graphqlOperation(queries.invoicesByProposalId, {
					proposalId: getUpdatedProposal.id,
					filter: { activeFlag: { eq: true } },
				})
			)
			const sortedInvoices = orderBy(items, ['invoiceNumber'], ['asc'])
			await dispatch(
				setCurrentDraftInvoiceId(
					getUpdatedProposal?.drafts?.[index]?.invoiceId ?? null
				)
			)
			await dispatch(setCurrentProposalInvoices(sortedInvoices))
		} catch (err) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const importInvoices = createAsyncThunk<
	void,
	{ invoicesData: string },
	AsyncThunkConfig
>(
	'invoices/importInvoices',
	async ({ invoicesData }, { getState, dispatch, rejectWithValue }) => {
		const tInvoices = i18next.getFixedT(null, 'invoices')
		try {
			const parsedInvoiceData = JSON.parse(
				invoicesData
			) as ImportedInvoice[]

			const { auth } = getState()
			const tenantId = auth.userTableInfo?.currentCompanyTenantId
			if (!tenantId) {
				return rejectWithValue('Tenant ID is not set')
			}
			if (parsedInvoiceData?.length > 0) {
				// parsedInvoiceData.forEach(async invoice =>
				for (const invoice of parsedInvoiceData) {
					const {
						data: { getContact },
					} = await graphqlRequest<{ getContact: ReduxContact }>(
						graphqlOperation(queries.getContact, {
							id: invoice.clientId,
						})
					)

					const {
						data: { getCompany: companyDetails },
					} = await graphqlRequest<{ getCompany: ReduxCompany }>(
						graphqlOperation(queries.getCompany, {
							id: tenantId,
						})
					)
					const {
						invoiceCount,
						invoiceNumber: newInvoiceNumber,
						invoicingYear: newInvoicingyear,
					} = getNextInvoiceCounter(companyDetails)
					const newCompanyDetails = {
						id: companyDetails.id,
						invoiceCount,
						invoicingYear: newInvoicingyear,
					}

					const year = dayjs(newInvoicingyear).year()
					const newInvoice: CreateInvoicesInput = {
						...invoice,
						id: nanoid(),
						tenantId,
						activeFlag: true,
						proposalId: `Uploaded-${nanoid()}`,
						clientId: invoice.clientId,
						paymentStatus: invoice.paymentStatus ?? 'Pending',
						invoiceType: 'original',
						isDetailed: invoice.isDetailed ?? false,
						showBidLetter: false,
						creditInvoiceGenerated: false,
						title: `Invoice ${newInvoiceNumber}`,
						invoiceNumber: `${
							companyDetails?.invoiceNumberPrefix ?? 'INV'
						}-${year}-${newInvoiceNumber}`,
						quoteName: 'Uploaded',
						quoteType: 'frenchApfp',
						projectName: 'Uploaded',
						proposalsDraft: [],
						clientName: getContact.name,
						balancePercentage: invoice.balancePercentage ?? 0,
						balanceAmount: invoice.balanceAmount ?? 0,
					}
					newInvoice.invoiceDate = dayjs(invoice.invoiceDate)
						.toDate()
						.toISOString()
					newInvoice.invoiceDueDate = dayjs(
						invoice.invoiceDueDate
					).toISOString()

					const allContacts = getContact.contactDetails || []
					let currentContact:
						| NonNullable<ReduxContact['contactDetails']>[number]
						| undefined
					if (allContacts.length > 0) {
						currentContact = allContacts.find(
							(contact) => contact?.id === invoice.contactId
						)
					}
					let clientName = ''
					newInvoice.clientEmail = ''
					if (currentContact) {
						clientName = currentContact?.fullName ?? ''
						newInvoice.clientEmail = currentContact?.email ?? ''
					}

					const clientAddress = `${getContact.line01}${
						getContact.line01 && '-'
					}${getContact.line02}${getContact.line02 && '- '}${
						getContact.country
					}`
					newInvoice.clientId = invoice.clientId
					newInvoice.contactId = invoice.contactId
					newInvoice.clientName = getContact.name
					newInvoice.contactName = clientName
					//   newInvoice.clientEmail = getContact.email
					newInvoice.clientAddress = clientAddress
					newInvoice.clientCountry = getContact.country
					newInvoice.clientIdNumber = getContact.idnumber
					newInvoice.clientVatNumber = getContact.vatNumber
					newInvoice.clientCurrency = getContact.currency
					newInvoice.clientTaxRate = getContact.taxRate
					newInvoice.clientNote = getContact.note
					newInvoice.isDetailed = invoice.isDetailed ?? false
					newInvoice.showBidLetter = invoice.showBidLetter
					newInvoice.isClientTaxRate = invoice.isClientTaxRate

					const bankProfile = getBankAccountProfile(
						companyDetails,
						invoice.bankProfileNumber
					)
					if (bankProfile.profileId !== 'empty') {
						newInvoice.invoicesBankProfileName =
							bankProfile.bankProfileName
						newInvoice.invoicesBank = bankProfile.bank
						newInvoice.invoicesIban = bankProfile.iban
						newInvoice.invoicesBic = bankProfile.bic
						newInvoice.invoicesAccountName = bankProfile.accountName
					}
					console.log('newInvoice: ', newInvoice)
					console.log('newCompanyDetails: ', newCompanyDetails)

					await API.graphql(
						graphqlOperation(
							customMutations.createInvoicesSimplified,
							{
								input: newInvoice,
							}
						)
					)
					await updateCompanyInfoWithDeltaSyncSuppress(
						newCompanyDetails
					)
				}
				makeSuccessToast(tInvoices('toasts.createdInvoice'))
			}
		} catch (err) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const updateInvoice = createAsyncThunk<
	void,
	UpdateInvoiceArgs,
	AsyncThunkConfig
>(
	'invoices/updateInvoice',
	async (
		{ proposalData, formData, currentProposal, index },
		{ dispatch, rejectWithValue }
	) => {
		const tInvoices = i18next.getFixedT(null, 'invoices')
		try {
			const {
				data: { getInvoices },
			} = await graphqlRequest<{ getInvoices: InvoiceWithContact }>(
				graphqlOperation(queries.getInvoices, {
					id: currentProposal.drafts?.[index]?.invoiceId,
				})
			)
			const {
				data: { getProposals },
			} = await graphqlRequest<{ getProposals: ReduxProposals }>(
				graphqlOperation(queries.getProposals, {
					id: currentProposal.id,
				})
			)
			const updatedInvoice: UpdateInvoicesInput = {
				id: getInvoices.id,
			}
			const updatedInvoiceValues = updatedInvoice as Record<
				string,
				unknown
			>
			const originalDrafts = getInvoices as unknown as Record<
				string,
				unknown
			>
			const copyProposalData: Record<string, unknown> &
				Partial<InvoiceDraftFormValue> = { ...proposalData }
			delete copyProposalData.amount
			delete copyProposalData.proposalAmountPercentage
			delete copyProposalData.readyStatus

			for (const property of Object.keys(copyProposalData)) {
				if (
					!isEqual(
						copyProposalData[property],
						originalDrafts[property]
					)
				) {
					updatedInvoiceValues[property] = copyProposalData[property]
					if (
						property === 'invoiceDueDate' &&
						getInvoices.paymentStatus !== 'Paid' &&
						getInvoices.paymentStatus !== 'Archived'
					) {
						if (
							dayjs(copyProposalData.invoiceDueDate).diff(
								dayjs(),
								'day'
							) >= 0
						) {
							updatedInvoice.paymentStatus = 'Pending'
						} else if (
							dayjs(copyProposalData.invoiceDueDate).diff(
								dayjs(),
								'day'
							) < 0
						) {
							updatedInvoice.paymentStatus = 'Overdue'
						}
					}
				}
			}

			for (let i = 0; i < Number.parseInt(formData.invoiceCount); i++) {
				formData.drafts[i].invoiceId =
					getProposals.drafts?.[i]?.invoiceId
				if (formData.drafts[i].invoiceDate === '') {
					formData.drafts[i].invoiceDate = null
				}
				if (formData.drafts[i].invoiceDueDate === '') {
					formData.drafts[i].invoiceDueDate = null
				}
			}
			updatedInvoice.proposalsDraft =
				formData.drafts as unknown as DraftsInput[]
			await API.graphql(
				graphqlOperation(customMutations.updateInvoicesSimplified, {
					input: updatedInvoice,
				})
			)
			dispatch(fetchInvoices({}))
			makeSuccessToast(tInvoices('toasts.updatedInvoice'))
			const {
				data: {
					invoicesByProposalId: { items },
				},
			} = await graphqlRequest<{
				invoicesByProposalId: { items: ReduxInvoices[] }
			}>(
				graphqlOperation(queries.invoicesByProposalId, {
					proposalId: getProposals.id,
					filter: { activeFlag: { eq: true } },
				})
			)
			const sortedInvoices = orderBy(items, ['invoiceNumber'], ['asc'])
			await dispatch(setCurrentDraftInvoiceId(getInvoices.id))
			await dispatch(setCurrentProposalInvoices(sortedInvoices))
		} catch (err) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const updatePaymentStatus = createAsyncThunk<
	void,
	PaymentStatusArgs,
	AsyncThunkConfig
>(
	'invoices/updatePaymentStatus',
	async (
		{ date, currentInvoice, startDate, endDate },
		{ getState, dispatch, rejectWithValue }
	) => {
		const tInvoices = i18next.getFixedT(null, 'invoices')
		try {
			const { auth } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User Table Info is not set')
			}
			const {
				data: { getInvoices },
			} = await graphqlRequest<{ getInvoices: InvoiceWithContact }>(
				graphqlOperation(queries.getInvoices, { id: currentInvoice })
			)
			delete getInvoices._deleted
			delete getInvoices._lastChangedAt
			delete getInvoices.contact
			delete getInvoices.proposals
			getInvoices.paymentStatus = 'Paid'
			getInvoices.paidDate = dayjs(date).toISOString()
			await API.graphql(
				graphqlOperation(customMutations.updateInvoicesSimplified, {
					input: getInvoices,
				})
			)
			if (
				getInvoices.invoiceType === 'credit' &&
				getInvoices.originalInvoiceId
			) {
				await recomputeOriginalInvoiceStatus({
					tenantId: userTableInfo.currentCompanyTenantId,
					originalInvoiceId: getInvoices.originalInvoiceId,
				})
			}
			const logInput = {
				id: nanoid(),
				activeFlag: true,
				tenantId: userTableInfo.currentCompanyTenantId,
				name: userTableInfo?.email,
				exportDate: dayjs().toISOString(),
				exportType: `Invoice ${
					getInvoices?.invoiceNumber ?? ''
				} Marked as Paid`,
				loggedLineId: getInvoices?.id,
			}
			const {
				data: { createExportInfo },
			} = await graphqlRequest<{ createExportInfo: unknown }>(
				graphqlOperation(mutations.createExportInfo, {
					input: logInput,
				})
			)

			dispatch(fetchInvoices({ startDate, endDate }))
			makeSuccessToast(tInvoices('toasts.markPaid'))
		} catch (err) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
export const markAsUnpaid = createAsyncThunk<
	void,
	InvoiceIdDateRangeArgs,
	AsyncThunkConfig
>(
	'invoices/markAsUnpaid',
	async (
		{ currentInvoice, startDate, endDate },
		{ getState, dispatch, rejectWithValue }
	) => {
		const tInvoices = i18next.getFixedT(null, 'invoices')
		try {
			const { auth } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User Table Info is not set')
			}
			const {
				data: { getInvoices },
			} = await graphqlRequest<{ getInvoices: InvoiceWithContact }>(
				graphqlOperation(queries.getInvoices, { id: currentInvoice })
			)
			delete getInvoices._deleted
			delete getInvoices._lastChangedAt
			delete getInvoices.contact
			delete getInvoices.proposals
			if (getInvoices.paymentStatus !== 'Archived') {
				getInvoices.paymentStatus = getDueBasedInvoiceStatus(
					getInvoices.invoiceDueDate
				)
			}
			getInvoices.paidDate = null
			await API.graphql(
				graphqlOperation(customMutations.updateInvoicesSimplified, {
					input: getInvoices,
				})
			)
			if (
				getInvoices.invoiceType === 'credit' &&
				getInvoices.originalInvoiceId
			) {
				await recomputeOriginalInvoiceStatus({
					tenantId: userTableInfo.currentCompanyTenantId,
					originalInvoiceId: getInvoices.originalInvoiceId,
				})
			}
			const logInput = {
				id: nanoid(),
				activeFlag: true,
				tenantId: userTableInfo.currentCompanyTenantId,
				name: userTableInfo?.email,
				exportDate: dayjs().toISOString(),
				exportType: `Invoice ${
					getInvoices?.invoiceNumber ?? ''
				} Marked as Unpaid`,
				loggedLineId: getInvoices?.id,
			}
			const {
				data: { createExportInfo },
			} = await graphqlRequest<{ createExportInfo: unknown }>(
				graphqlOperation(mutations.createExportInfo, {
					input: logInput,
				})
			)
			dispatch(fetchInvoices({ startDate, endDate }))
			makeSuccessToast(tInvoices('toasts.markUnpaid'))
		} catch (err) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
export const invoiceDailyUpdatePaymentStatus = createAsyncThunk<
	void,
	InvoiceDailyUpdateArgs,
	AsyncThunkConfig
>(
	'invoices/invoiceDailyUpdatePaymentStatus',
	async (
		{ invoiceData, startDate, endDate },
		{ dispatch, rejectWithValue }
	) => {
		try {
			if (invoiceData?.length > 0) {
				for (const eachInv of invoiceData) {
					const {
						data: { getInvoices },
					} = await graphqlRequest<{
						getInvoices: ReduxInvoices | null
					}>(
						graphqlOperation(queries.getInvoices, {
							id: eachInv?.id,
						})
					)
					if (!getInvoices) continue
					const updatedInvoice = {
						id: getInvoices.id,
						paymentStatus: 'Overdue',
					}
					await API.graphql(
						graphqlOperation(
							customMutations.updateInvoicesSimplified,
							{
								input: updatedInvoice,
							}
						)
					)
				}
			}
			dispatch(fetchInvoices({ startDate, endDate }))
		} catch (err) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
export const updateArchiveStatus = createAsyncThunk<
	void,
	InvoiceIdDateRangeArgs,
	AsyncThunkConfig
>(
	'invoices/updateArchiveStatus',
	async (
		{ currentInvoice, startDate, endDate },
		{ dispatch, rejectWithValue }
	) => {
		try {
			const {
				data: { getInvoices },
			} = await graphqlRequest<{ getInvoices: InvoiceWithContact }>(
				graphqlOperation(queries.getInvoices, { id: currentInvoice })
			)
			delete getInvoices._deleted
			delete getInvoices._lastChangedAt
			delete getInvoices.contact
			delete getInvoices.proposals
			let dueStatus = ''
			if (dayjs(getInvoices.invoiceDueDate).diff(dayjs(), 'day') >= 0) {
				dueStatus = 'Pending'
			} else if (
				dayjs(getInvoices.invoiceDueDate).diff(dayjs(), 'day') < 0
			) {
				dueStatus = 'Overdue'
			}
			getInvoices.paymentStatus =
				getInvoices.paymentStatus !== 'Archived'
					? 'Archived'
					: dueStatus
			getInvoices.archivedDate = dayjs().toISOString()
			await API.graphql(
				graphqlOperation(customMutations.updateInvoicesSimplified, {
					input: getInvoices,
				})
			)
			dispatch(fetchInvoices({ startDate, endDate }))
			makeSuccessToast(
				`Invoice succesfully  ${
					getInvoices.paymentStatus === 'Archived'
						? 'archived'
						: 'unarchived'
				}`
			)
		} catch (err) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
export const viewPdf = createAsyncThunk<void, ViewPdfArgs, AsyncThunkConfig>(
	'invoices/viewPdf',
	async ({ row }, { dispatch, rejectWithValue }) => {
		try {
			let invId = row.id
			if (row.invoiceType === 'credit') {
				invId = row.originalInvoiceId ?? row.id
			}
			const {
				data: { getInvoices: originalInvoiceData },
			} = await graphqlRequest<{ getInvoices: ReduxInvoices }>(
				graphqlOperation(queries.getInvoices, {
					id: invId,
				})
			)
			dispatch(setCurrentInvoice(row))
			if (
				row.quoteName !== 'Uploaded' &&
				row.projectName !== 'Uploaded'
			) {
				// Set the proposal so RTK Query hooks can access quoteId
				dispatch(setCurrentProposal(row.proposals ?? null))
			} else {
				dispatch(setCurrentProposal(row as unknown as ReduxProposals))
				dispatch(setViewImportedPdfModalOpenStatus(true))
			}
			dispatch(setOriginalInvoiceData(originalInvoiceData))
		} catch (err) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const updateInvoiceLastExportedDate = createAsyncThunk<
	void,
	UpdateInvoiceLastExportedDateArgs,
	AsyncThunkConfig
>(
	'purchases/updateInvoiceLastExportedDate',
	async (
		{ invoiceIds, startDate, endDate },
		{ getState, rejectWithValue, dispatch }
	) => {
		console.log('invoiceIds: ', invoiceIds)
		const { auth } = getState()

		const tenantId = auth.userTableInfo?.currentCompanyTenantId
		let errorMessage = msg
		try {
			if (!tenantId) {
				return rejectWithValue('Tenant ID is not set')
			}
			if (!invoiceIds || invoiceIds.length === 0) return
			const {
				data: { updateInvoiceLastExportedDate },
			} = await graphqlRequest<{
				updateInvoiceLastExportedDate?: ResolverStatusResponse | null
			}>(
				graphqlOperation(mutations.updateInvoiceLastExportedDate, {
					companyTenantId: tenantId,
					invoiceIdArray: invoiceIds,
				})
			)
			const { status, isSuccessful } = updateInvoiceLastExportedDate ?? {}
			const resolvedStatus = status ?? 'Failed'
			if (isSuccessful) {
				await dispatch(fetchInvoices({ startDate, endDate }))
			} else {
				if (resolvedStatus !== 'Failed') {
					errorMessage = resolvedStatus
				}
				captureExceptionAndThrow(new Error(resolvedStatus), {
					location: 'invoices.updateInvoiceLastExportedDate',
					companyTenantId: tenantId,
					invoiceCount: invoiceIds.length,
					timeRange: { startDate, endDate },
				})
			}
		} catch (err) {
			const errorMessage = getErrorMessage(err)
			console.error(
				'errorMessage for updateInvoiceLastExportedDate: ',
				errorMessage
			)
			makeErrorToast(` ${errorMessage}`)
			return rejectWithValue(err)
		}
	}
)

const {
	aws_user_files_s3_bucket_region: region,
	aws_user_files_s3_bucket: bucket,
} = config
async function saveS3(
	pdfBlob: Blob | File | ArrayBuffer | string,
	fileName: string
): Promise<S3SaveResult | null> {
	try {
		const cred = await Auth.currentCredentials()
		const key = `${nanoid()}__${fileName}`
		const fileUploadDetails = {
			bucket: typeof bucket === 'string' ? bucket : '',
			key,
			region: typeof region === 'string' ? region : '',
			identityId: cred.identityId,
		}
		console.log('fileUploadDetails: ', fileUploadDetails)
		console.log('fileName: ', fileName)

		await Storage.put(key, pdfBlob, {
			level: 'protected',
			contentType: 'application/pdf',
		})
		return { fileUploadDetails, fileName }
	} catch (err) {
		console.log('err: ', err)
		return null
	}
}

export const sendClientInvoice = createAsyncThunk<
	void,
	SendClientInvoiceArgs,
	AsyncThunkConfig
>(
	'invoices/sendClientInvoice',
	async (
		{ invoiceId, emailData, pdfBlob, currentLanguage, startDate, endDate },
		{ getState, rejectWithValue, dispatch }
	) => {
		console.log('emailData: ', emailData)
		console.log('invoiceId: ', invoiceId)
		const { auth } = getState()
		const tenantId = auth.userTableInfo?.currentCompanyTenantId
		let errorMessage = msg
		const tInvoices = i18next.getFixedT(null, 'invoices')
		try {
			if (!tenantId) {
				return rejectWithValue('Tenant ID is not set')
			}
			const {
				data: { getCompany: companyDetails },
			} = await graphqlRequest<{ getCompany: ReduxCompany }>(
				graphqlOperation(queries.getCompany, {
					id: tenantId,
				})
			)
			const {
				data: { getInvoices: currentInvoice },
			} = await graphqlRequest<{ getInvoices: ReduxInvoices | null }>(
				graphqlOperation(queries.getInvoices, {
					id: invoiceId,
				})
			)
			if (!currentInvoice) {
				makeErrorToast('Cant find the invoice')
				return
			}
			if (!pdfBlob) {
				makeErrorToast('Missing pdf attachment')
				return
			}
			const uploadResult = await saveS3(
				pdfBlob,
				`${currentInvoice.invoiceNumber}_${currentInvoice?.id}.pdf`
			)
			if (!uploadResult) {
				makeErrorToast('Failed to upload pdf attachment')
				return
			}
			const { fileUploadDetails } = uploadResult

			const bucketName = `${fileUploadDetails?.bucket}/protected/${fileUploadDetails?.identityId}`
			const key = fileUploadDetails?.key

			const {
				data: { sendClientInvoiceEmail },
			} = await graphqlRequest<{
				sendClientInvoiceEmail?: ResolverStatusResponse | null
			}>(
				graphqlOperation(mutations.sendClientInvoiceEmail, {
					companyTenantId: tenantId,
					invoiceId: invoiceId,
					username: auth?.userTableInfo?.email,
					bucketName,
					key,
					emailData,
					currentLanguage,
					companyName: companyDetails?.name,
				})
			)
			const { status, isSuccessful } = sendClientInvoiceEmail ?? {}
			const resolvedStatus = status ?? 'Failed'
			if (isSuccessful) {
				await dispatch(fetchInvoices({ startDate, endDate }))
				makeSuccessToast(tInvoices('toasts.emailSent'))
			} else {
				if (resolvedStatus !== 'Failed') {
					errorMessage = resolvedStatus
				}
				captureExceptionAndThrow(new Error(resolvedStatus), {
					location: 'invoices.sendClientInvoiceEmail',
					companyTenantId: tenantId,
					invoiceId,
					emailData,
					currentLanguage,
				})
			}
		} catch (err) {
			const errorMessage = getErrorMessage(err)
			console.error('errorMessage for sendClientInvoice: ', errorMessage)
			makeErrorToast(` ${errorMessage}`)
			return rejectWithValue(err)
		}
	}
)
export const saveEmailAsDraft = createAsyncThunk<
	void,
	SaveEmailAsDraftArgs,
	AsyncThunkConfig
>(
	'invoices/saveEmailAsDraft',
	async (
		{ invoiceId, draftEmailData, startDate, endDate },
		{ getState, rejectWithValue, dispatch }
	) => {
		console.log('draftEmailData: ', draftEmailData)
		console.log('invoiceId: ', invoiceId)
		const { auth } = getState()
		const tenantId = auth.userTableInfo?.currentCompanyTenantId
		if (!tenantId) {
			return rejectWithValue('Tenant ID is not set')
		}
		const errorMessage = msg
		const tInvoices = i18next.getFixedT(null, 'invoices')
		try {
			if (!invoiceId) {
				makeErrorToast('No invoice id')
				return
			}
			const {
				data: { getInvoices: currentInvoice },
			} = await graphqlRequest<{ getInvoices: ReduxInvoices | null }>(
				graphqlOperation(queries.getInvoices, {
					id: invoiceId,
				})
			)
			if (!currentInvoice) {
				makeErrorToast('Cant find the invoice')
				return
			}
			const updInv = {
				id: currentInvoice?.id,
				draftEmailData,
			}
			console.log('updInv: ', updInv)
			await API.graphql(
				graphqlOperation(customMutations.updateInvoicesSimplified, {
					input: updInv,
				})
			)
			await dispatch(fetchInvoices({ startDate, endDate }))
			makeSuccessToast(tInvoices('toasts.emailSavedAsDraft'))
		} catch (err) {
			const errorMessage = getErrorMessage(err)
			console.error('errorMessage for sendClientInvoice: ', errorMessage)
			makeErrorToast(` ${errorMessage}`)
			return rejectWithValue(err)
		}
	}
)

export const invoicesSlice = createSlice({
	name: 'invoices',
	initialState,
	reducers: {
		setInvoiceArchivedConfirmationDialogOpenStatus: (
			state,
			action: PayloadAction<DialogStatusPayload>
		) => {
			state.invoiceArchivedConfirmationDialogOpenStatus =
				action.payload.status
		},
		setInvoicePaidConfirmationDialogOpenStatus: (
			state,
			action: PayloadAction<DialogStatusPayload>
		) => {
			state.invoicePaidConfirmationDialogOpenStatus =
				action.payload.status
		},
		setInvoiceUnpaidConfirmationDialogOpenStatus: (
			state,
			action: PayloadAction<DialogStatusPayload>
		) => {
			state.invoiceUnpaidConfirmationDialogOpenStatus =
				action.payload.status
		},
		setInvoiceDownloadConfirmationDialogOpenStatus: (
			state,
			action: PayloadAction<DialogStatusPayload>
		) => {
			state.invoiceDownloadConfirmationDialogOpenStatus =
				action.payload.status
		},
		setInvoiceDownloadConfirmationFromPagesDialogOpenStatus: (
			state,
			action: PayloadAction<DialogStatusPayload>
		) => {
			state.invoiceDownloadConfirmationFromPagesDialogOpenStatus =
				action.payload.status
		},
		setCreditInvoiceModalOpenStatus: (
			state,
			action: PayloadAction<boolean>
		) => {
			state.creditInvoiceModalOpenStatus = action.payload
		},
		setImportInvoicesModalOpen: (state, action: PayloadAction<boolean>) => {
			state.importInvoicesModalOpen = action.payload
		},
		setFetchInvoicesApiStatus: (state) => {
			state.fetchInvoicesApiStatus = 'loading'
		},
		setCurrentInvoice: (
			state,
			action: PayloadAction<ReduxInvoices | null>
		) => {
			state.currentInvoice = action.payload
		},
		setOriginalInvoiceData: (
			state,
			action: PayloadAction<ReduxInvoices | null>
		) => {
			state.originalInvoiceData = action.payload
		},
		setViewPdfModalOpenStatus: (state, action: PayloadAction<boolean>) => {
			state.viewPdfModalOpenStatus = action.payload
		},
		setViewImportedPdfModalOpenStatus: (
			state,
			action: PayloadAction<boolean>
		) => {
			state.viewImportedPdfModalOpenStatus = action.payload
		},
		setPreivewCreditInvoiceModalOpenStatus: (
			state,
			action: PayloadAction<DialogStatusPayload>
		) => {
			state.preivewCreditInvoiceModalOpenStatus = action.payload.status
		},
		setConfirmInvoiceExportDialogOpenStatus: (
			state,
			action: PayloadAction<boolean>
		) => {
			state.confirmInvoiceExportDialogOpenStatus = action.payload
		},
		setSendClientInvoiceEmailModalOpenStatus: (
			state,
			action: PayloadAction<boolean>
		) => {
			state.sendClientInvoiceEmailModalOpenStatus = action.payload
		},
		setSyncInvoiceContactModalOpenStatus: (
			state,
			action: PayloadAction<boolean>
		) => {
			state.syncInvoiceContactModalOpenStatus = action.payload
		},
		setInvoiceToSync: (
			state,
			action: PayloadAction<ReduxInvoices | null>
		) => {
			state.invoiceToSync = action.payload
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchInvoices.pending, (state) => {
			state.fetchInvoicesApiStatus = 'loading'
		})
		builder.addCase(fetchInvoices.fulfilled, (state, action) => {
			state.fetchInvoicesApiStatus = 'succeeded'
			state.invoicesData = action.payload
		})
		builder.addCase(fetchInvoices.rejected, (state, action) => {
			state.fetchInvoicesApiStatus = 'failed'
			state.error = action.error.message ?? null
		})
		builder.addCase(fetchUnpaidInvoices.pending, (state) => {
			state.fetchUnpaidInvoicesApiStatus = 'loading'
		})
		builder.addCase(fetchUnpaidInvoices.fulfilled, (state, action) => {
			state.fetchUnpaidInvoicesApiStatus = 'succeeded'
			state.unpaidInvoicesData = action.payload
		})
		builder.addCase(fetchUnpaidInvoices.rejected, (state, action) => {
			state.fetchUnpaidInvoicesApiStatus = 'failed'
			state.error = action.error.message ?? null
		})
		builder.addCase(createInvoice.pending, (state) => {
			state.createInvoiceApiStatus = 'loading'
		})
		builder.addCase(createInvoice.fulfilled, (state) => {
			state.createInvoiceApiStatus = 'succeeded'
			state.invoiceDownloadConfirmationDialogOpenStatus = true
		})
		builder.addCase(createInvoice.rejected, (state, action) => {
			state.createInvoiceApiStatus = 'failed'
			state.invoiceDownloadConfirmationDialogOpenStatus = false
			state.error = action.error.message ?? null
		})
		builder.addCase(createCreditInvoice.pending, (state) => {
			state.createInvoiceApiStatus = 'loading'
		})
		builder.addCase(createCreditInvoice.fulfilled, (state) => {
			state.createInvoiceApiStatus = 'succeeded'
			state.creditInvoiceModalOpenStatus = false
		})
		builder.addCase(createCreditInvoice.rejected, (state, action) => {
			state.createInvoiceApiStatus = 'failed'
			state.error = action.error.message ?? null
		})
		builder.addCase(previewCreditInvoice.pending, (state) => {
			state.previewCreditInvoiceApiStatus = 'loading'
		})
		builder.addCase(previewCreditInvoice.fulfilled, (state, action) => {
			state.previewCreditInvoiceApiStatus = 'succeeded'
			state.previewCreditInvoiceData = action.payload
			state.preivewCreditInvoiceModalOpenStatus = true
		})
		builder.addCase(previewCreditInvoice.rejected, (state, action) => {
			state.previewCreditInvoiceApiStatus = 'failed'
			state.error = action.error.message ?? null
		})
		builder.addCase(importInvoices.pending, (state) => {
			state.importInvoicesApiStatus = 'loading'
		})
		builder.addCase(importInvoices.fulfilled, (state) => {
			state.importInvoicesApiStatus = 'succeeded'
			state.importInvoicesModalOpen = false
		})
		builder.addCase(importInvoices.rejected, (state, action) => {
			state.importInvoicesApiStatus = 'failed'
			state.error = action.error.message ?? null
		})
		builder.addCase(updateInvoice.pending, (state) => {
			state.updateInvoiceApiStatus = 'loading'
		})
		builder.addCase(updateInvoice.fulfilled, (state) => {
			state.updateInvoiceApiStatus = 'succeeded'
			state.invoiceDownloadConfirmationDialogOpenStatus = true
		})
		builder.addCase(updateInvoice.rejected, (state, action) => {
			state.updateInvoiceApiStatus = 'failed'
			state.invoiceDownloadConfirmationDialogOpenStatus = false
			state.error = action.error.message ?? null
		})
		builder.addCase(updatePaymentStatus.pending, (state) => {
			state.updatePaymentApiStatus = 'loading'
		})
		builder.addCase(updatePaymentStatus.fulfilled, (state) => {
			state.updatePaymentApiStatus = 'succeeded'
			state.invoicePaidConfirmationDialogOpenStatus = false
		})
		builder.addCase(updatePaymentStatus.rejected, (state, action) => {
			state.updatePaymentApiStatus = 'failed'
			state.invoicePaidConfirmationDialogOpenStatus = false
			state.error = action.error.message ?? null
		})
		builder.addCase(markAsUnpaid.pending, (state) => {
			state.updatePaymentApiStatus = 'loading'
		})
		builder.addCase(markAsUnpaid.fulfilled, (state) => {
			state.updatePaymentApiStatus = 'succeeded'
			state.invoiceUnpaidConfirmationDialogOpenStatus = false
		})
		builder.addCase(markAsUnpaid.rejected, (state, action) => {
			state.updatePaymentApiStatus = 'failed'
			state.invoiceUnpaidConfirmationDialogOpenStatus = false
			state.error = action.error.message ?? null
		})
		builder.addCase(updateArchiveStatus.pending, (state) => {
			state.updateArchiveApiStatus = 'loading'
		})
		builder.addCase(updateArchiveStatus.fulfilled, (state) => {
			state.updateArchiveApiStatus = 'succeeded'
			state.invoiceArchivedConfirmationDialogOpenStatus = false
		})
		builder.addCase(updateArchiveStatus.rejected, (state, action) => {
			state.updateArchiveApiStatus = 'failed'
			state.invoiceArchivedConfirmationDialogOpenStatus = false
			state.error = action.error.message ?? null
		})
		builder.addCase(viewPdf.pending, (state) => {
			state.viewPdfStatus = 'loading'
		})
		builder.addCase(viewPdf.fulfilled, (state, action) => {
			state.viewPdfStatus = 'succeeded'
			if (action.meta.arg.actionType !== 'sendEmail') {
				state.viewPdfModalOpenStatus = true
			} else {
				state.sendClientInvoiceEmailModalOpenStatus = true
			}
		})
		builder.addCase(viewPdf.rejected, (state, action) => {
			state.viewPdfStatus = 'failed'
			if (action.meta.arg.actionType !== 'sendEmail') {
				state.viewPdfModalOpenStatus = false
			} else {
				state.sendClientInvoiceEmailModalOpenStatus = false
			}
			state.error = action.error.message ?? null
		})
		builder.addCase(updateInvoiceLastExportedDate.pending, (state) => {
			state.updateInvoiceLastExportedDate = 'loading'
			state.updateInvoiceLastExportedDateApiStatus = 'loading'
		})
		builder.addCase(updateInvoiceLastExportedDate.fulfilled, (state) => {
			state.updateInvoiceLastExportedDate = 'succeeded'
			state.updateInvoiceLastExportedDateApiStatus = 'succeeded'
		})
		builder.addCase(
			updateInvoiceLastExportedDate.rejected,
			(state, action) => {
				state.updateInvoiceLastExportedDate = 'failed'
				state.updateInvoiceLastExportedDateApiStatus = 'failed'
				state.error = action.error.message ?? null
			}
		)
		builder.addCase(sendClientInvoice.pending, (state) => {
			state.sendClientInvoiceApiStatus = 'loading'
		})
		builder.addCase(sendClientInvoice.fulfilled, (state) => {
			state.sendClientInvoiceApiStatus = 'succeeded'
			state.sendClientInvoiceEmailModalOpenStatus = false
		})
		builder.addCase(sendClientInvoice.rejected, (state, action) => {
			state.sendClientInvoiceApiStatus = 'failed'
			state.error = action.error.message ?? null
		})
		builder.addCase(saveEmailAsDraft.pending, (state) => {
			state.saveEmailAsDraftApiStatus = 'loading'
		})
		builder.addCase(saveEmailAsDraft.fulfilled, (state) => {
			state.saveEmailAsDraftApiStatus = 'succeeded'
			state.sendClientInvoiceEmailModalOpenStatus = false
		})
		builder.addCase(saveEmailAsDraft.rejected, (state, action) => {
			state.saveEmailAsDraftApiStatus = 'failed'
			state.error = action.error.message ?? null
		})
	},
})

export const {
	setInvoiceArchivedConfirmationDialogOpenStatus,
	setInvoicePaidConfirmationDialogOpenStatus,
	setFetchInvoicesApiStatus,
	setInvoiceUnpaidConfirmationDialogOpenStatus,
	setInvoiceDownloadConfirmationDialogOpenStatus,
	setInvoiceDownloadConfirmationFromPagesDialogOpenStatus,
	setCreditInvoiceModalOpenStatus,
	setImportInvoicesModalOpen,
	setCurrentInvoice,
	setOriginalInvoiceData,
	setViewPdfModalOpenStatus,
	setViewImportedPdfModalOpenStatus,
	setPreivewCreditInvoiceModalOpenStatus,
	setConfirmInvoiceExportDialogOpenStatus,
	setSendClientInvoiceEmailModalOpenStatus,
	setSyncInvoiceContactModalOpenStatus,
	setInvoiceToSync,
} = invoicesSlice.actions

export default invoicesSlice.reducer
